import React, { useState, memo, useEffect } from 'react';
import { MenuTrigger, ActionButton, View, Text } from '@adobe/react-spectrum';
import CalendarIcon from '@spectrum-icons/workflow/Calendar';
import { parseDate, CalendarDate } from '@internationalized/date';
import { calendarDateToISOString } from '../utils/dateUtils';
import styles from '../FormField.module.scss';
import { CalendarRange } from '../../../Fields/Calendar';

interface DateRangePickerComponentProps {
  field: any;
  value:
    | { start?: string; end?: string }
    | { type: 'months'; months: string[] };
  onChange: (
    value:
      | { type: 'date'; start: string; end: string }
      | { type: 'months'; months: string[] }
  ) => void;
  disablePastDate?: boolean;
  enforceEndDateAfterStart?: boolean;
  validationState?: 'valid' | 'invalid';
  errorMessage?: string;
  isOverdue?: boolean; // ✅ NEW: Flag to indicate if dates are overdue (past current date/month/quarter)
}

function getCurrentMonthRange() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // CalendarDate months are 1-based
  const start = parseDate(`${year}-${String(month).padStart(2, '0')}-01`);
  const end = parseDate(
    `${year}-${String(month).padStart(2, '0')}-${String(
      new Date(year, month, 0).getDate()
    ).padStart(2, '0')}`
  );
  return { start, end };
}

/**
 * DateRangePickerComponent: Allows selection of a start and end date, and sends data as two fields
 */
export const DateRangePickerComponent: React.FC<DateRangePickerComponentProps> =
  memo(
    ({
      field,
      value,
      onChange,
      disablePastDate = false,
      enforceEndDateAfterStart = false,
      validationState,
      errorMessage,
      isOverdue = false // ✅ NEW: Default to false if not provided
    }) => {
      // Helper function to convert month string to CalendarDate range
      const convertMonthsToDateRange = (months: string[]) => {
        if (!months || months.length === 0) {
          return { start: undefined, end: undefined };
        }

        const monthNames = [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec'
        ];

        // Parse first month
        const firstMonth = months[0];
        const firstParts = firstMonth.split(' ');
        const firstMonthName = firstParts[0];
        const firstYear = parseInt(firstParts[1]);
        const firstMonthIndex = monthNames.indexOf(firstMonthName);

        // Parse last month (or use first month if only one)
        const lastMonth = months[months.length - 1];
        const lastParts = lastMonth.split(' ');
        const lastMonthName = lastParts[0];
        const lastYear = parseInt(lastParts[1]);
        const lastMonthIndex = monthNames.indexOf(lastMonthName);

        if (firstMonthIndex === -1 || lastMonthIndex === -1) {
          return { start: undefined, end: undefined };
        }

        // Create start date (first day of first month)
        const start = parseDate(
          `${firstYear}-${String(firstMonthIndex + 1).padStart(2, '0')}-01`
        );

        // Create end date (last day of last month)
        const lastDayOfMonth = new Date(
          lastYear,
          lastMonthIndex + 1,
          0
        ).getDate();
        const end = parseDate(
          `${lastYear}-${String(lastMonthIndex + 1).padStart(2, '0')}-${String(
            lastDayOfMonth
          ).padStart(2, '0')}`
        );

        return { start, end };
      };

      // Parse initial value to CalendarDate objects for the calendar
      const getInitialRange = () => {
        if (value && 'type' in value && value.type === 'months') {
          // Convert month-based value to date range for highlighting
          return convertMonthsToDateRange(value.months);
        } else if (
          (value as { start?: string })?.start ||
          (value as { end?: string })?.end
        ) {
          // Handle date-based values
          return {
            start: (value as { start?: string })?.start
              ? parseDate((value as { start?: string })?.start!)
              : undefined,
            end: (value as { end?: string })?.end
              ? parseDate((value as { end?: string })?.end!)
              : undefined
          };
        }
        return { start: undefined, end: undefined };
      };

      const initialRange = getInitialRange();
      const [isOpen, setIsOpen] = useState(false);
      const [dateRange, setDateRange] = useState<{
        start?: CalendarDate;
        end?: CalendarDate;
      }>(initialRange);

      // Keep local state in sync with parent value
      useEffect(() => {
        if (value && 'type' in value && value.type === 'months') {
          // Convert month-based value to date range for calendar highlighting
          const monthRange = convertMonthsToDateRange(value.months);
          setDateRange(monthRange);
        } else if (
          (value as { start?: string })?.start &&
          (value as { end?: string })?.end
        ) {
          setDateRange({
            start: parseDate((value as { start: string }).start),
            end: parseDate((value as { end: string }).end)
          });
        } else if (
          !value ||
          (!(value as { start?: string })?.start &&
            !(value as { end?: string })?.end)
        ) {
          setDateRange({ start: undefined, end: undefined });
        }
      }, [value]);

      // Helper to format CalendarDate to ISO string - use timezone-safe utility
      const toISO = (date: CalendarDate) => calendarDateToISOString(date);

      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec'
      ];

      const handleRangeChange = (newRange: {
        start: CalendarDate;
        end: CalendarDate;
      }) => {
        setDateRange(newRange);
        if (newRange.start && newRange.end) {
          // If the range is a month range (start is 1st, end is last day of month)
          const isFullMonthRange =
            newRange.start.day === 1 &&
            new Date(newRange.end.year, newRange.end.month, 0).getDate() ===
              newRange.end.day;
          if (isFullMonthRange) {
            // Build array of month names between start and end
            let months: string[] = [];
            let year = newRange.start.year;
            let month = newRange.start.month - 1; // CalendarDate is 1-based
            const endYear = newRange.end.year;
            const endMonth = newRange.end.month - 1;
            while (year < endYear || (year === endYear && month <= endMonth)) {
              months.push(`${monthNames[month]} ${year}`);
              month++;
              if (month > 11) {
                month = 0;
                year++;
              }
            }
            const payload = {
              type: 'months' as const,
              months
            };

            onChange(payload);
            setIsOpen(false); // Close for month selection
          } else {
            // Default: store as date range
            const payload = {
              type: 'date' as const,
              start: toISO(newRange.start),
              end: toISO(newRange.end)
            };

            onChange(payload);
            // Close if it's a valid date range (start and end are set)
            setIsOpen(false);
          }
        }
      };

      // Determine if we are showing placeholder text (no selection)
      const isPlaceholder = (() => {
        if (
          value &&
          'type' in value &&
          value.type === 'months' &&
          value.months &&
          value.months.length > 0
        )
          return false;
        if (dateRange?.start && dateRange?.end) return false;
        return true;
      })();

      return (
        <View>
          <Text UNSAFE_className={styles.fieldLabel}>
            {field.label}
            {field.required && <span className={styles.required}> *</span>}
          </Text>
          <MenuTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
            <ActionButton
              UNSAFE_className={`${styles.calendarButton} ${
                validationState === 'invalid' ? styles.invalid : ''
              }`}
              onPress={() => setIsOpen(true)}
            >
              <Text
                UNSAFE_className={`calendar-text ${
                  isPlaceholder ? styles.placeholderText : ''
                }`}
                UNSAFE_style={{
                  color: isOverdue && !isPlaceholder ? '#D7373F' : undefined // ✅ Red text if overdue (same color as overdue cards)
                }}
              >
                {(() => {
                  if (
                    value &&
                    'type' in value &&
                    value.type === 'months' &&
                    value.months &&
                    value.months.length > 0
                  ) {
                    return value.months.length === 1
                      ? value.months[0]
                      : `${value.months[0]} - ${
                          value.months[value.months.length - 1]
                        }`;
                  } else if (dateRange?.start && dateRange?.end) {
                    // If start and end dates are the same, show only single date
                    const startISO = toISO(dateRange.start);
                    const endISO = toISO(dateRange.end);
                    return startISO === endISO
                      ? startISO
                      : `${startISO} to ${endISO}`;
                  } else {
                    return 'Select date or date range';
                  }
                })()}
              </Text>
              <CalendarIcon />
            </ActionButton>
            <CalendarRange
              defaultRange={
                dateRange.start && dateRange.end
                  ? { start: dateRange.start, end: dateRange.end }
                  : undefined
              }
              onCalendarRangeChange={handleRangeChange}
              skipTriggerButton={true}
              hideYearList={true}
              hideQuarterList={true}
              disablePastDate={disablePastDate}
              enforceEndDateAfterStart={enforceEndDateAfterStart}
            />
          </MenuTrigger>
          {validationState === 'invalid' && errorMessage && (
            <Text UNSAFE_className={styles.errorText}>{errorMessage}</Text>
          )}
        </View>
      );
    },
    (prevProps, nextProps) => prevProps.value === nextProps.value
  );

export default DateRangePickerComponent;
